import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useBetSlip, SlipItem } from '../context/BetSlipContext';
import { placeBet, queueBspSingle, queueOwnBotSingle, betfairErrorMessage, PlaceBetRequest } from '../api/betfair';
import './BetSlipSidebar.css';
import './RaceCompact.css';
import MultiplesSlip from './MultiplesSlip';
import SlipRunnerCard from './SlipRunnerCard';
import { multiMoney } from '../api/multiples';
import {isPaperAccount} from '../api/paper';

const round2 = (n: number) => Math.round(n * 100) / 100;

type PlaceResult = { id: string; ok: boolean; message: string };

// ownBot: a non-owner user's horse BACK singles go into their own bot, never the owner's Auto queue.
// Owner 2026-10-13: horses clicked on Bucket Next Up appear as runner cards (Win / Place / Lay win / Lay place
// stakes + Add in multi). One button at the bottom submits every single and the multi together - no second confirm.
const BetSlipSidebar: React.FC<{ ownBot?: boolean }> = ({ ownBot = false }) => {
  const {
    items, status, statusError, open, cards,
    removeItem, updateItem, clear, setOpen, refreshStatus,
    multipleItems,clearMultiples,
  } = useBetSlip();

  const [placing, setPlacing] = useState(false);
  const [results, setResults] = useState<PlaceResult[]>([]);
  const [multi, setMulti] = useState<{ready:boolean;cents:number;mode:string;count:number}>({ready:false,cents:0,mode:'paper',count:0});
  const multiSubmit = useRef<null|(()=>Promise<void>)>(null);

  const paper=isPaperAccount();
  const plainItems = items.filter(it => !it.cardKey);
  const count = items.length + cards.length;
  const usesBspQueue=(it:SlipItem)=>!paper&&it.side==='BACK'&&(!it.racingCode||it.racingCode==='HORSE');
  const fundedItems = items.filter(it => Number(it.stake) > 0);
  const allBsp=fundedItems.length>0&&fundedItems.every(usesBspQueue);
  const balance = status?.account.balance ?? null;
  const connected = !!status?.configured;

  const totalStake = useMemo(
    () => round2(items.reduce((s, it) => s + (Number(it.stake) || 0), 0)),
    [items]
  );
  // Money actually at risk: a BACK risks its stake; a LAY risks its liability.
  const totalRisk = useMemo(
    () => round2(items.reduce((s, it) => {
      const stake = Number(it.stake) || 0;
      const price = Number(it.price) || 0;
      return s + (it.side === 'LAY' ? stake * (price - 1) : stake);
    }, 0)),
    [items]
  );

  const [bump, setBump] = React.useState(false);
  useEffect(() => { const h = () => { setBump(true); setTimeout(() => setBump(false), 500); }; window.addEventListener('betslip-bump', h); return () => window.removeEventListener('betslip-bump', h); }, []);
  useEffect(() => {
    document.body.classList.toggle('slip-open', open);
    return () => document.body.classList.remove('slip-open');
  }, [open]);

  if (!open) {
    if (count === 0 && multipleItems.length === 0) return null;
    return (
      <button className={'slip-fab'+(bump?' bump':'')} onClick={() => setOpen(true)}>
        <span>🧾 Bet Slip{multipleItems.length>1?` · multi ${new Set(multipleItems.map((m:any)=>m.win_market_id)).size} legs`:''}</span> <span className="slip-fab-count">{count+multipleItems.length}</span>{typeof window!=='undefined'&&window.innerWidth<=600&&<span style={{fontWeight:600}}>Open ▲</span>}
      </button>
    );
  }

  const placeAllSingles = async (): Promise<PlaceResult[]> => {
    const out: PlaceResult[] = [];
    for (const it of fundedItems) {
      try {
        const req: PlaceBetRequest = {
          marketId: it.marketId,
          selectionId: it.selectionId,
          runnerName: it.runnerName,
          side: it.side,
          size: round2(it.stake),
          price: Number(it.price),
          // Take-SP is the house default: any portion unmatched at the off goes to the SP auction.
          persistenceType: it.persistenceType || 'MARKET_ON_CLOSE',
          marketName: it.marketName,
          meetingName: it.meetingName,
          raceNumber: it.raceNumber,
          date: it.date,
        };
        if(usesBspQueue(it)){
          const mt=it.marketType||(it.marketName==='PLACE'?'PLACE':'WIN');
          const queued=ownBot?await queueOwnBotSingle(req,mt):await queueBspSingle(req,mt);
          out.push({id:it.id,ok:ownBot?queued.live===true:true,message:it.runnerName+' '+mt+': added to '+(ownBot?'your bot':'Auto')+' at BSP, $'+Number(queued.stake).toFixed(2)+' · '+(ownBot?queued.note:queued.queue_state)});
          continue;
        }
        if (ownBot) throw new Error('Only horse racing BACK singles can be added to your bot.');
        const res = await placeBet(req);
        const b = res.bet;
        out.push({
          id: it.id,
          ok: true,
          message: paper ? `${it.runnerName}: saved to your paper account` : `${it.runnerName} ${it.side} ${it.marketType||''}: ${b.orderStatus}, matched $${b.sizeMatched.toFixed(2)}${b.averagePriceMatched ? ` @ ${b.averagePriceMatched.toFixed(2)}` : ''}`,
        });
      } catch (err) {
        out.push({ id: it.id, ok: false, message: `${it.runnerName}: ${betfairErrorMessage(err)}` });
      }
    }
    setResults(out);
    // Drop the selections that placed successfully; keep failures for retry.
    const failed = new Set(out.filter((r) => !r.ok).map((r) => r.id));
    fundedItems.filter((it) => !failed.has(it.id)).forEach((it) => removeItem(it.id));
    if (out.some((r) => r.ok)) refreshStatus();
    return out;
  };

  const submitAll = async () => {
    setPlacing(true); setResults([]);
    try {
      const out = fundedItems.length ? await placeAllSingles() : [];
      if (multi.ready && multiSubmit.current) await multiSubmit.current();
      // Owner 2026-10-13: once everything is pushed, clear the slip (runner cards included). Failures stay for retry.
      if (!out.some(r => !r.ok)) clear();
    } finally { setPlacing(false); }
  };

  const renderItem = (it: SlipItem) => {
    const stake = Number(it.stake) || 0;
    const price = Number(it.price) || 0;
    const isLay = it.side === 'LAY';
    const ret = round2(isLay ? stake * (price - 1) : stake * price);
    const result = results.find((r) => r.id === it.id);
    return (
      <div className="slip-item" key={it.id}>
        <div className="slip-item-head">
          <span className={`slip-bettype ${isLay ? 'lay' : 'back'}`}>{it.side}</span>
          <div className="slip-item-name">
            <strong>{it.number ? `${it.number}. ` : ''}{it.runnerName}</strong>
            <span className="slip-item-race">
              {it.meetingName}{it.raceNumber ? ` · R${it.raceNumber}` : ''} · {it.marketType || it.marketName || 'Exchange'}{it.persistenceType==='LAPSE'?' · Limit order · Unmatched lapses at start':''}
            </span>
          </div>
          <button className="slip-remove" aria-label="Remove" onClick={() => removeItem(it.id)}>×</button>
        </div>
        <div className="slip-item-fields">
          <label className="slip-field">
            <span>Odds</span>
            <div className="slip-stepper">
              <button type="button" onClick={() => updateItem(it.id, { price: round2(Math.max(1.01, it.price - 0.05)) })}>−</button>
              <input type="number" step="0.01" min="1.01" value={it.price}
                onChange={(e) => updateItem(it.id, { price: parseFloat(e.target.value) || 0 })} />
              <button type="button" onClick={() => updateItem(it.id, { price: round2(it.price + 0.05) })}>+</button>
            </div>
          </label>
          <label className="slip-field">
            <span>Stake $</span>
            <div className="slip-stepper">
              <button type="button" onClick={() => updateItem(it.id, { stake: round2(Math.max(0, it.stake - 5)) })}>−</button>
              <input type="number" step="1" min="0" value={it.stake}
                onChange={(e) => updateItem(it.id, { stake: parseFloat(e.target.value) || 0 })} />
              <button type="button" onClick={() => updateItem(it.id, { stake: round2(it.stake + 5) })}>+</button>
            </div>
          </label>
        </div>
        <div className="slip-quick">
          {[5, 10, 25, 50].map((q) => (
            <button key={q} type="button" onClick={() => updateItem(it.id, { stake: q })}>${q}</button>
          ))}
          <span className="slip-item-return">
            {isLay ? 'Liability' : 'Returns'} <strong>${ret.toFixed(2)}</strong>
          </span>
        </div>
        {result && (
          <div className={`slip-item-result ${result.ok ? 'ok' : 'err'}`}>{result.message}</div>
        )}
      </div>
    );
  };

  const singlesNeedBetfair = fundedItems.length > 0 && !connected;
  const canSubmit = !placing && (fundedItems.length > 0 || multi.ready) && !singlesNeedBetfair;
  const parts = [
    fundedItems.length ? `${fundedItems.length} single${fundedItems.length > 1 ? 's' : ''} $${totalStake.toFixed(2)}` : '',
    multi.ready ? `${multi.mode} multi ${multiMoney(multi.cents)}` : '',
  ].filter(Boolean).join(' + ');
  const verb = !fundedItems.length || allBsp ? 'Add to Auto' : 'Submit all';

  return (
    <aside className="bet-slip-sidebar unified-slip" aria-label="Bet slip">
      <div className="slip-header">
        <h3>Bet Slip {count > 0 && <span className="slip-count">{count}</span>}</h3>
        <div className="slip-header-actions">
          {(count > 0 || multipleItems.length > 0) && <button className="slip-link" onClick={()=>{clear();clearMultiples();setResults([]);}}>Clear</button>}
          <button className="slip-close" aria-label="Collapse" onClick={() => setOpen(false)}>×</button>
        </div>
      </div>

      <div className={`slip-status ${connected ? 'ok' : 'off'}`}>
        <span className="slip-status-dot" />
        {connected ? (
          <>{paper?'Paper account · Available':'Betfair connected · Balance'} <strong>${balance != null ? balance.toFixed(2) : '—'}</strong></>
        ) : (
          <>Betfair not connected{statusError ? ` — ${statusError}` : ''}</>
        )}
      </div>

      <div className="slip-scroll">
      {cards.length > 0 && <div className="slip-body srcards">{cards.map(c => <SlipRunnerCard key={c.key} card={c} />)}</div>}
      {plainItems.length > 0 && <>
        <h4 className="slip-section-title">Selections · Singles</h4>
        <div className="slip-body">{plainItems.map(renderItem)}</div>
      </>}
      {count === 0 && (
        <div className="slip-empty">
          <p>Your bet slip is empty.</p>
          <p className="slip-empty-hint">Click a horse on Bucket Next Up, or select a blue Win or Place price on a race page.</p>
        </div>
      )}
      <MultiplesSlip embedded onState={setMulti} submitRef={multiSubmit} />
      <div className="slip-footer slip-submit-all">
        <div className="slip-totals">
          <div><span>Singles stake</span><strong>${totalStake.toFixed(2)}</strong></div>
          <div><span>Singles at risk</span><strong>${totalRisk.toFixed(2)}</strong></div>
          {multi.count > 0 && <div><span>Multi ({multi.mode})</span><strong>{multiMoney(multi.cents)}</strong></div>}
        </div>
        {singlesNeedBetfair && <div className="slip-warn">Connect Betfair to place singles.</div>}
        {connected && balance != null && totalRisk > balance && (
          <div className="slip-warn">Singles at risk exceed your balance (${balance.toFixed(2)}).</div>
        )}
        <button className="slip-place" disabled={!canSubmit} onClick={() => void submitAll()}>
          {placing ? 'Submitting…' : parts ? `${verb} · ${parts}` : 'Enter a stake or build a multi'}
        </button>
        {fundedItems.some(it => !usesBspQueue(it)) && !paper && <p className="multi-help">Lay singles are sent to the Betfair exchange now (unmatched goes to SP at the off). Back singles go to Auto at BSP.</p>}
        {results.map(r=><p key={r.id} role={r.ok?'status':'alert'} className={'slip-item-result '+(r.ok?'ok':'err')}>{r.message}</p>)}
        {results.length > 0 && <button className="slip-link slip-clear-results" onClick={() => setResults([])}>Dismiss results</button>}
      </div>
      </div>
    </aside>
  );
};

export default BetSlipSidebar;
